/* eslint-disable max-lines */
import { PastShiftDetailsRoute } from "@src/appV2/redesign/WorkerShifts/PastShiftDetails/Route";
import { ShiftCancelPage } from "@src/appV2/redesign/WorkerShifts/ShiftCancel/ShiftCancelPage";
import { ShiftDetailsRoute } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/Route";
import { REFERRAL_REDEEM_CODE_PATH, ReferralRedeemCodePage } from "@src/appV2/Referral/RedeemCode";
import { type ReactElement } from "react";
import { Redirect, Route, Switch, useLocation, useRouteMatch } from "react-router-dom";

import { AccountRoutes } from "../Accounts";
import { ChatListContainer, LegacyChatListContainer } from "../Chat/ChatList/Container";
import { ChatPage } from "../Chat/ChatPage";
import { LegacyChatPage } from "../Chat/LegacyChatPage";
import { CHAT_LIST_PATH, CHAT_PAGE_PATH, WORKPLACE_GROUP_CHAT_PAGE_PATH } from "../Chat/paths";
import { DEBUG_PATH, DebugRoutes } from "../Debug";
import { NotFoundPage } from "../Errors/NotFound";
import { CbhFeatureFlag } from "../FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "../FeatureFlags/useCbhFlag";
import { RATE_NEGOTIATION_LIST_PATH } from "../Negotiations/path";
import { RateNegotiationListPage } from "../Negotiations/RateNegotiationListPage";
import { NewsFeedPage } from "../NewsFeed/NewsFeedPage";
import { NEWS_FEED_FULL_PATH } from "../NewsFeed/paths";
import { DelayedReviewContainer } from "../PostShiftReview/DelayedReview/DelayedReviewContainer";
import { ACCOUNTS_PATH } from "../redesign/Accounts/paths";
import { PayoutSettingsContainer } from "../redesign/Accounts/PayoutSettings/Container";
import { PAYOUT_SETTINGS_BASE_PATH } from "../redesign/Accounts/PayoutSettings/paths";
import { BackgroundCheckContainer } from "../redesign/BackgroundChecks/BackgroundCheckContainer";
import { ClipboardScoreRoutes } from "../redesign/ClipboardScore/Routes";
import { CLIPBOARD_SCORE_PATH } from "../redesign/ClipboardScore/utils/paths";
import { DocumentsContainer } from "../redesign/Documents/Container";
import { BACKGROUND_CHECK_BASE_PATH, DOCUMENTS_BASE_PATH } from "../redesign/Documents/paths";
import { InHomeContainer } from "../redesign/InHome/Container";
import { IN_HOME_BOOKED_VISITS_PATH } from "../redesign/InHome/paths";
import { Navbar } from "../redesign/Navbar";
import { NotificationPreferencePage } from "../redesign/NotificationPreferences/Page";
import { NOTIFICATIONS_PREFERENCE_PATH } from "../redesign/NotificationPreferences/paths";
import { useShouldShowJobsFirst } from "../redesign/Onboarding/hooks/useShouldShowJobsFirst";
import { EarningsContainer } from "../redesign/Payments/Earnings/components/Container";
import { EARNINGS_PATH } from "../redesign/Payments/Earnings/paths";
import { FailedPayoutContainer } from "../redesign/Payments/FailedPayouts/Container";
import { FAILED_PAYOUT_BASE_PATH } from "../redesign/Payments/FailedPayouts/paths";
import { StripeOnboardingContainer } from "../redesign/Payments/Onboarding/Container";
import { STRIPE_ONBOARDING_BASE_PATH } from "../redesign/Payments/Onboarding/paths";
import { ProofOfEarningsPage } from "../redesign/Payments/ProofOfEarnings/Page";
import { PROOF_OF_EARNINGS_BASE_PATH } from "../redesign/Payments/ProofOfEarnings/paths";
import { PLACEMENTS_BASE_PATH } from "../redesign/Placements/paths";
import { PlacementRoutes } from "../redesign/Placements/Routes";
import { SHIFT_BLOCKS_PATH } from "../redesign/ShiftDiscovery/Blocks/paths";
import { ShiftDiscoveryContainer } from "../redesign/ShiftDiscovery/Container";
import {
  SHIFT_DISCOVERY_PATH,
  SHIFT_TRANSFER_PATH,
  WORK_WITH_FRIENDS_PATH,
} from "../redesign/ShiftDiscovery/paths";
import { SOCIAL_PATH } from "../redesign/Social/paths";
import { SocialContainer } from "../redesign/Social/SocialContainer";
import { SUPPORT_PATH } from "../redesign/Support/paths";
import { SupportRouter } from "../redesign/Support/Router";
import { WorkerAvailabilityProvider } from "../redesign/WorkerAvailability/context";
import { WorkerAvailabilityPage } from "../redesign/WorkerAvailability/Page";
import { WORKER_AVAILABILITIES_PATH } from "../redesign/WorkerAvailability/paths";
import { BookingRequestsRouteContainer } from "../redesign/WorkerShifts/BookingRequests/BookingRequestsRouteContainer";
import { BreakPaymentRequestsPage } from "../redesign/WorkerShifts/BreakPaymentRequests/Page";
import { CreateSentHomeRequestPage } from "../redesign/WorkerShifts/CreateSentHomeRequest/Page";
import { WorkerShiftsPage } from "../redesign/WorkerShifts/Page";
import {
  BOOKING_REQUESTS_PATH,
  BREAK_PAYMENT_REQUESTS_PATH,
  CREATE_SENT_HOME_REQUEST_PATH,
  CREATE_SHIFT_VERIFICATION_REQUEST_PATH,
  MY_SHIFTS_FULL_PATH,
  MY_SHIFTS_PATH,
  PAST_SHIFT_DETAILS_PATH,
  SENT_HOME_REQUESTS_PATH,
  SHIFT_CANCEL_PATH,
  SHIFT_DETAILS_PATH,
  SHIFT_HISTORY_PATH,
  SHIFT_INVITES_PATH,
  SUBMIT_UNVERIFIED_TIMESHEET_PATH,
  UNVERIFIED_SHIFTS_PATH,
} from "../redesign/WorkerShifts/path";
import { SentHomeRequestsPage } from "../redesign/WorkerShifts/SentHomeRequests/Page";
import { SubmitUnverifiedTimesheetPage } from "../redesign/WorkerShifts/ShiftDetails/Timekeeping/SubmitUnverifiedTimesheetPage";
import { ShiftHistoryPage } from "../redesign/WorkerShifts/ShiftHistory/Page";
import { ShiftInvitesRouteContainer } from "../redesign/WorkerShifts/ShiftInvites/ShiftInvitesRouteContainer";
import { ReceiveTransferPage } from "../redesign/WorkerShifts/TransferShifts/Transferee/ReceiveTransferPage";
import { UnverifiedShiftsPage } from "../redesign/WorkerShifts/UnverifiedShifts/Page";
import { ShiftVerificationRequestPage } from "../redesign/WorkerShifts/UploadUnverifiedTimesheet/Page";
import { WORKLACE_QUIZ_BASE_PATH } from "../redesign/WorkplaceQuiz/paths";
import { WorkplaceQuizContainer } from "../redesign/WorkplaceQuiz/WorkplaceQuizContainer";
import { WorkWithFriendsContainer } from "../redesign/WorkWithFriends/Container";
import { WorkerReferralsPage } from "../Referral/Page";
import { REFERRAL_SHARE_LINK_PATH, WORKER_REFERRALS_PATH } from "../Referral/path";
import { ShiftBlockRoutes } from "../ShiftBlocks/Routes";
import { ZENDESK_SSO_BRIDGE_PATH } from "../Zendesk/paths";
import { ZendeskSsoBridge } from "../Zendesk/ZendeskSsoBridge";

const NavigationPaths = new Set<string>([MY_SHIFTS_FULL_PATH]);

export function AppV2PrivateRoutes(): ReactElement {
  const match = useRouteMatch();
  const location = useLocation();
  const showJobsFirst = useShouldShowJobsFirst();
  const currentPathIsANavigationPath = NavigationPaths.has(location.pathname);
  const navbar = currentPathIsANavigationPath ? <Navbar /> : undefined;
  const isChatReactQueryEnabled = useCbhFlag(CbhFeatureFlag.WORKER_CHAT_V2, {
    defaultValue: false,
  });

  return (
    <>
      <Switch>
        <Route path={`${match.url}/${DEBUG_PATH}`}>
          <DebugRoutes />
        </Route>
        <Route path={`${match.url}/${ACCOUNTS_PATH}`}>
          <AccountRoutes />
        </Route>
        <Route exact path={`${match.url}/${ZENDESK_SSO_BRIDGE_PATH}`}>
          <ZendeskSsoBridge />
        </Route>
        <Route exact path={`${match.url}/${UNVERIFIED_SHIFTS_PATH}`}>
          <UnverifiedShiftsPage />
        </Route>
        <Route exact path={`${match.url}/${BREAK_PAYMENT_REQUESTS_PATH}`}>
          <BreakPaymentRequestsPage />
        </Route>
        <Route exact path={`${match.url}/${SENT_HOME_REQUESTS_PATH}`}>
          <SentHomeRequestsPage />
        </Route>
        <Route exact path={`${match.url}/${CREATE_SENT_HOME_REQUEST_PATH}`}>
          <CreateSentHomeRequestPage />
        </Route>
        <Route exact path={`${match.url}/${CREATE_SHIFT_VERIFICATION_REQUEST_PATH}`}>
          <ShiftVerificationRequestPage />
        </Route>
        <Route exact path={`${match.url}/${SUBMIT_UNVERIFIED_TIMESHEET_PATH}`}>
          <SubmitUnverifiedTimesheetPage />
        </Route>
        <Route exact path={`${match.url}/${SHIFT_HISTORY_PATH}`}>
          <ShiftHistoryPage />
        </Route>
        <Route path={`${match.url}/${PAST_SHIFT_DETAILS_PATH}`}>
          <PastShiftDetailsRoute />
        </Route>
        <Route path={`${match.url}/${SHIFT_CANCEL_PATH}`}>
          <ShiftCancelPage />
        </Route>
        <Route path={`${match.url}/${SHIFT_DETAILS_PATH}`}>
          <ShiftDetailsRoute />
        </Route>
        <Route path={NEWS_FEED_FULL_PATH}>
          <NewsFeedPage />
        </Route>
        <Route path={`${match.url}/${FAILED_PAYOUT_BASE_PATH}`}>
          <FailedPayoutContainer />
        </Route>
        <Route path={`${match.url}/${STRIPE_ONBOARDING_BASE_PATH}`}>
          <StripeOnboardingContainer />
        </Route>
        <Route path={`${match.url}/${SHIFT_INVITES_PATH}`}>
          <ShiftInvitesRouteContainer />
        </Route>
        <Route path={`${match.url}/${BOOKING_REQUESTS_PATH}`}>
          <BookingRequestsRouteContainer />
        </Route>
        <Route path={`${match.url}/${MY_SHIFTS_PATH}`}>
          <WorkerShiftsPage />
        </Route>
        <Route exact path={`${match.url}/${WORKER_AVAILABILITIES_PATH}`}>
          <WorkerAvailabilityProvider>
            <WorkerAvailabilityPage />
          </WorkerAvailabilityProvider>
        </Route>
        <Route path={`${match.url}/${SHIFT_BLOCKS_PATH}`}>
          <ShiftBlockRoutes />
        </Route>
        <Route exact path={`${match.url}/${REFERRAL_REDEEM_CODE_PATH}`}>
          <ReferralRedeemCodePage />
        </Route>
        <Route path={`${match.url}/${SHIFT_DISCOVERY_PATH}`}>
          <DelayedReviewContainer />
          <ShiftDiscoveryContainer />
        </Route>
        <Route path={`${match.url}/${WORK_WITH_FRIENDS_PATH}`}>
          <WorkWithFriendsContainer />
        </Route>
        <Route path={`${match.url}/${BACKGROUND_CHECK_BASE_PATH}`}>
          <BackgroundCheckContainer />
        </Route>
        <Route path={`${match.url}/${PLACEMENTS_BASE_PATH}`}>
          <PlacementRoutes />
        </Route>
        <Route exact path={`${match.url}/${CHAT_PAGE_PATH}`}>
          {isChatReactQueryEnabled ? <ChatPage /> : <LegacyChatPage />}
        </Route>
        <Route exact path={`${match.url}/${WORKPLACE_GROUP_CHAT_PAGE_PATH}`}>
          <ChatPage />
        </Route>
        <Route path={`${match.url}/${CHAT_LIST_PATH}`}>
          {isChatReactQueryEnabled ? <ChatListContainer /> : <LegacyChatListContainer />}
        </Route>
        <Route path={`${match.url}/${SOCIAL_PATH}`}>
          <SocialContainer />
        </Route>
        <Route path={`${match.url}/${DOCUMENTS_BASE_PATH}`}>
          <DocumentsContainer />
        </Route>
        <Route path={`${match.url}/${PAYOUT_SETTINGS_BASE_PATH}`}>
          <PayoutSettingsContainer />
        </Route>
        <Route path={`${match.url}/${EARNINGS_PATH}`}>
          <EarningsContainer />
        </Route>
        <Route exact path={`${match.url}/${PROOF_OF_EARNINGS_BASE_PATH}`}>
          <ProofOfEarningsPage />
        </Route>
        <Route path={`${match.url}/${IN_HOME_BOOKED_VISITS_PATH}`}>
          <InHomeContainer />
        </Route>
        <Route path={`${match.url}/${SHIFT_TRANSFER_PATH}/:shiftId`}>
          <ReceiveTransferPage />;
        </Route>
        <Route path={`${match.url}/${CLIPBOARD_SCORE_PATH}`}>
          <ClipboardScoreRoutes />
        </Route>
        {/* Always mount Support routes; feature gating happens inside SupportRouter */}
        <Route path={`${match.url}/${SUPPORT_PATH}`}>
          <SupportRouter />
        </Route>
        <Route exact path={match.url}>
          <Redirect
            to={`${match.url}/${showJobsFirst ? PLACEMENTS_BASE_PATH : SHIFT_DISCOVERY_PATH}`}
          />
        </Route>
        <Route path={`${match.url}/${WORKER_REFERRALS_PATH}`}>
          <WorkerReferralsPage />
        </Route>
        <Route path={`${match.url}/${REFERRAL_SHARE_LINK_PATH}`}>
          <WorkerReferralsPage />
        </Route>
        <Route path={`${match.url}/${WORKLACE_QUIZ_BASE_PATH}`}>
          <WorkplaceQuizContainer />
        </Route>
        <Route path={`${match.url}/${RATE_NEGOTIATION_LIST_PATH}`}>
          <RateNegotiationListPage />
        </Route>
        <Route
          exact
          path={`${match.url}/${NOTIFICATIONS_PREFERENCE_PATH}`}
          component={NotificationPreferencePage}
        />
        <Route>
          <NotFoundPage />
        </Route>
      </Switch>
      {navbar}
    </>
  );
}
/* eslint-enable max-lines */
